import React, { useCallback, useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import ChallengeCard from "../components/ChallengeCard";
import ChallengeDetail from "../components/ChallengeDetail";
import { getContext, getGeneration, listGenerations } from "../lib/api";
import { toast } from "sonner";

export default function History() {
    const [generations, setGenerations] = useState([]);
    const [selectedGen, setSelectedGen] = useState(null);
    const [genChallenges, setGenChallenges] = useState([]);
    const [selectedChallenge, setSelectedChallenge] = useState(null);
    const [context, setContext] = useState(null);

    useEffect(() => {
        listGenerations()
            .then(setGenerations)
            .catch(() => toast.error("Failed to load history"));
        getContext(3)
            .then(setContext)
            .catch(() => {});
    }, []);

    const openGen = useCallback(async (id) => {
        try {
            const data = await getGeneration(id);
            setSelectedGen(data.generation);
            setGenChallenges(data.challenges);
        } catch (e) {
            toast.error("Failed to load generation");
        }
    }, []);

    return (
        <AppShell liveContext={context}>
            <div className="space-y-6">
                <div>
                    <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tighter">
                        Generation History
                    </h1>
                    <p className="text-white/60 text-sm mt-1">
                        Every run, indexed by gameweek and average quality.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* List */}
                    <div className="lg:col-span-5 bg-[#121212] border border-white/10 overflow-hidden">
                        <div className="px-4 py-3 border-b border-white/10 bg-[#0a0a0a] font-mono text-[10px] uppercase tracking-widest text-white/60 grid grid-cols-12 gap-2">
                            <div className="col-span-5">Timestamp</div>
                            <div className="col-span-2">GW</div>
                            <div className="col-span-2">Count</div>
                            <div className="col-span-3">Avg Q</div>
                        </div>
                        {generations.length === 0 ? (
                            <div className="p-6 text-white/40 text-sm">No generations yet.</div>
                        ) : (
                            generations.map((g) => {
                                const active = selectedGen?.id === g.id;
                                const q = g.avgQuality || 0;
                                const qColor =
                                    q >= 80 ? "#9EE8B4" : q >= 70 ? "#FFF3C2" : "#FF2A2A";
                                return (
                                    <button
                                        key={g.id}
                                        onClick={() => openGen(g.id)}
                                        className={`w-full text-left px-4 py-3 grid grid-cols-12 gap-2 border-b border-white/5 transition-colors ${
                                            active ? "bg-fa-yellow/10" : "hover:bg-white/5"
                                        }`}
                                        data-testid={`generation-row-${g.id}`}
                                    >
                                        <div className="col-span-5 font-mono text-xs text-white/80">
                                            {new Date(g.createdAt).toLocaleString()}
                                        </div>
                                        <div className="col-span-2 font-mono text-xs">
                                            GW{g.gameweek}
                                        </div>
                                        <div className="col-span-2 font-mono text-xs">
                                            {g.challengeIds?.length || 0}
                                        </div>
                                        <div
                                            className="col-span-3 font-mono text-xs font-bold"
                                            style={{ color: qColor }}
                                        >
                                            {q}
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>

                    {/* Selected run */}
                    <div className="lg:col-span-7 space-y-3">
                        {selectedGen ? (
                            <>
                                <div className="bg-[#121212] border border-white/10 p-4">
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                        Run · {selectedGen.id.slice(0, 8)}
                                    </div>
                                    <div className="font-display text-lg font-black uppercase mt-1">
                                        GW{selectedGen.gameweek} · {selectedGen.challengeIds.length}{" "}
                                        challenges · avg Q{selectedGen.avgQuality}
                                    </div>
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        {Object.entries(selectedGen.counts || {}).map(([t, n]) => (
                                            <div
                                                key={t}
                                                className="font-mono text-[10px] uppercase tracking-wider border border-white/10 px-2 py-0.5 bg-black"
                                            >
                                                {t}: {n}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {genChallenges.map((c, i) => (
                                        <ChallengeCard
                                            key={c.id}
                                            challenge={c}
                                            index={i}
                                            onClick={setSelectedChallenge}
                                        />
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="bg-[#121212] border border-dashed border-white/10 p-8 text-center">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                    Select a run
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ChallengeDetail
                challenge={selectedChallenge}
                open={!!selectedChallenge}
                onOpenChange={(v) => !v && setSelectedChallenge(null)}
                onChanged={() => selectedGen && openGen(selectedGen.id)}
            />
        </AppShell>
    );
}
